import "./App.css";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AppRoutes from "./router";
import ChatNotificationPopup from "./Component/ChatNotificationPopup";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import AppLogout from "./Component/AppLogout";
import { AppProvider } from "./Services/AppContext";
import "./consoleLogger";
import RawWebSocket from "./Websocker/RawWebSocket";
import { WEBSOCKET_URL } from "./Services/Constant";
import useStoreKv from "./Screen/redux/store/useStorekv";
import useStore from "./Screen/redux/store/useStore";
import { get } from "./Services/ApiHandler";
import { URLS } from "./Services/Constant";
function App() {
  const { set, kv, remove } = useStoreKv();
  const { setPermissions, user } = useStore();
  const dispatch = useDispatch();
  const alertMessage = useSelector((state) => state.alertMessage);
  const total_wallet_ref = useRef(kv.totalWallet);
  const navigate = useNavigate();
  const queryClient = new QueryClient();

  // ✅ Global notifications state lives here
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    remove("totalWallet");
  }, []);

  useEffect(() => {
    total_wallet_ref.current = kv.totalWallet;
  }, [kv.totalWallet]);

  useEffect(() => {
    handleSocket();
  }, [localStorage.getItem("token"), sessionStorage.getItem("token")]);

  const handleSocket = () => {
    let Tok = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (Tok) {
      RawWebSocket.connect(`${WEBSOCKET_URL}?token=${encodeURIComponent(Tok)}`);
      RawWebSocket.clearListeners();

      RawWebSocket.listen("connection_update", (e) => {
        set("onlineCount", e?.total_users ?? 0);
        if (!total_wallet_ref.current && total_wallet_ref.current !== 0)
          set("totalWallet", e?.total_wallet_amount ?? 0);
      });

      RawWebSocket.listen("admin_wallet_update", (e) => {
        set("totalWallet", e?.data?.total_amount ?? 0);
      });

      RawWebSocket.listen("admin_permissions", (e) => {
        if (String(e?.data?.admin_id) !== String(user?.id)) return;
        setPermissions(e?.data?.modules ?? []);
      });

      // ✅ chat_message listener here — AFTER clearListeners, so it's never wiped
      RawWebSocket.listen("chat_message", async (e) => {
        const data = e?.data;
        if (!data) return;
        if (data?.is_admin_sender === 1) return;

        // Fetch client info by client_id
        let client_name = `Client ${data.client_id}`;
        let mobile = "";

        try {
          const res = await get(`${URLS.CLIENT}?page=1&search=${data.client_id}`);
          // Exact match by id — avoids wrong results from partial search
          const matched = res?.data?.find((c) => c.id === data.client_id);
          if (matched) {
            client_name = matched.name || matched.client_name || client_name;
            mobile = matched.mobile || matched.phone || "";
          }
        } catch (err) {
          console.error("Failed to fetch client info:", err);
        }

        setNotifications((prev) => {
          const exists = prev.find((n) => n.client_id === data.client_id);
          if (exists) {
            return prev.map((n) =>
              n.client_id === data.client_id
                ? { ...n, message: data.message, id: `${data.client_id}_${Date.now()}` }
                : n
            );
          }

          const newNotif = {
            id: `${data.client_id}_${Date.now()}`,
            client_id: data.client_id,
            client_name,
            mobile,
            message: data.message || "New message",
          };

          const updated = [...prev, newNotif];
          return updated.length > 50 ? updated.slice(-50) : updated;
        });
      });

    } else {
      RawWebSocket.disconnect();
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AppProvider>
          <AppLogout>
            <AppRoutes />
            <ScrollToTop />
            {/* ✅ Pass notifications and setter down as props */}
            <ChatNotificationPopup
              notifications={notifications}
              setNotifications={setNotifications}
            />
          </AppLogout>
        </AppProvider>
      </LocalizationProvider>
    </QueryClientProvider>
  );
}

export default App;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};